import { useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useTranslation } from '@frontend/i18n';
import { AppLink } from '@frontend/settings-routing';
import { Breadcrumbs } from '@frontend/design-system';
import { VIEW_KEYS } from '../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../hooks/use-appointment-analytics-v2-store';

interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export const AppointmentAnalyticsBreadcrumb = () => {
  const { customTableView } = useSearch({ from: '/analytics/appointment' });
  const { changeView } = useAppointmentAnalyticsV2ShallowStore('changeView');

  const { t } = useTranslation('analytics');

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const baseCrumb: BreadcrumbItem[] = [
      { label: t('Analytics'), href: '/analytics/dashboard' },
      {
        label: t('Appointment Analytics'),
        href: `/analytics/appointment`,
        onClick: () => changeView(VIEW_KEYS.CHART),
      },
    ];

    if (customTableView === 'prospect-list') {
      return [...baseCrumb, { label: t('Prospect Patient List') }];
    }

    if (customTableView === 'schedule-requests') {
      return [...baseCrumb, { label: t('Schedule Requests') }];
    }

    return [];
  }, [t, customTableView, changeView]);

  return (
    <Breadcrumbs>
      {breadcrumbs.map((crumb, index) =>
        'href' in crumb ? (
          <Breadcrumbs.Path key={index} as={AppLink} to={crumb.href} onClick={crumb.onClick}>
            {crumb.label}
          </Breadcrumbs.Path>
        ) : (
          <Breadcrumbs.Path
            key={index}
            onClick={crumb.onClick}
            style={crumb.onClick ? { cursor: 'pointer' } : undefined}
          >
            {crumb.label}
          </Breadcrumbs.Path>
        )
      )}
    </Breadcrumbs>
  );
};
